import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { usePopupRef } from '@framework/hooks';
import { useCurrentUserEntity } from '@user/UserEntity/useCurrentUserEntity';
import { UserPagePathBuilder } from '@user/pages/UserPagePathBuilder';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import { classNames, generateDefaultUserIcon } from '@framework/utils';
import { AnonymousUserPopupMenu, RegularUserPopupMenu } from '@framework/ui/elements/TopNavigation';
type Props = {
  userId: string;
};

export const UserIconMenu: React.FC<Props> = ({ userId }: Props) => {
  const { t } = useTranslation();
  const { isOpen, popupRef, handleOpen } = usePopupRef<HTMLDivElement>(false);
  const navigate = useNavigate();

  const handleLogout = useCallback(() => {
    // 現在表示中のURLに対して logout パラメータを付与したURLにリダイレクトさせる
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set('logout', '1');
    const currentUrl = window.location.pathname + '?' + urlParams.toString();

    const params = new URLSearchParams();
    params.set('requestPath', currentUrl);
    navigate(`${UserPagePathBuilder.logoutPage()}?${params.toString()}`);
  }, [navigate]);

  const profile = useUserPublicProfile(userId);
  const userEntity = useCurrentUserEntity();

  // profile が取得できないときには、アイコン・メニューを表示できないので null を返す
  if (!profile) return null;

  return (
    <div className="relative">
      <button
        className={classNames(
          'flex h-12 w-12 items-center justify-center',
          'cursor-pointer rounded-lg bg-white shadow-md hover:bg-gray-200 focus:outline-none'
        )}
        onClick={handleOpen}
        aria-label={t('viewModel.userMenu.open')}
      >
        {profile.imageUrl ? (
          <img
            className="block size-8 rounded-full border-2 border-gray-400"
            src={profile.imageUrl}
            alt={profile.name}
          />
        ) : (
          <div className="block size-8 overflow-hidden rounded-full border-2 border-gray-400">
            {generateDefaultUserIcon({
              id: userId,
              name: profile.name,
              isAnonymous: profile.isAnonymous,
              size: 28,
            })}
          </div>
        )}
      </button>
      <div
        className={classNames(
          'absolute right-0 z-50 mt-2 w-64 rounded-lg bg-white p-1 shadow-xl',
          isOpen ? 'block' : 'hidden'
        )}
        ref={popupRef}
      >
        {/* UserEntity を取得できた場合には、通常ユーザ向けのメニューを表示する。それ以外は匿名ユーザ向けのメニューを表示する */}
        {userEntity ? (
          <RegularUserPopupMenu userEntity={userEntity} onLogout={handleLogout} />
        ) : (
          <AnonymousUserPopupMenu profile={profile} onLogout={handleLogout} />
        )}
      </div>
    </div>
  );
};
